import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';

import ValorMonetario from '../components/ValorMonetario';
import ApiPedidos from '../services/pedidosService';

export default function Financeiro() {
  const [pedidos, setPedidos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [pedidoEmProcessamento, setPedidoEmProcessamento] =
    useState(null);

  const [tipoProcessamento, setTipoProcessamento] =
    useState(null);

  const [modalCancelamento, setModalCancelamento] =
    useState(false);

  const [pedidoSelecionado, setPedidoSelecionado] =
    useState(null);

  const [motivoCancelamento, setMotivoCancelamento] =
    useState('');

  const [feedback, setFeedback] = useState({
    tipo: '',
    mensagem: '',
  });

  const user = JSON.parse(
    localStorage.getItem('user') || 'null'
  );

  useEffect(() => {
    if (!user?.id) {
      setFeedback({
        tipo: 'danger',
        mensagem: 'Usuário não identificado.',
      });

      setIsLoading(false);

      return;
    }

    fetchPagamentos();
  }, []);

  const fetchPagamentos = async () => {
    try {
      setIsLoading(true);

      const response =
        await ApiPedidos.listarPorUserId(
          user.id
        );

      setPedidos(
        response?.data ?? []
      );
    } catch (error) {
      console.error(
        'Erro ao buscar pedidos:',
        error
      );

      setPedidos([]);

      setFeedback({
        tipo: 'danger',
        mensagem:
          'Não foi possível carregar os pedidos.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatarData = (data) => {
    if (!data) {
      return '-';
    }

    try {
      /*
       * Evita alteração de dia causada
       * pelo timezone quando recebemos
       * somente YYYY-MM-DD.
       */
      if (/^\d{4}-\d{2}-\d{2}$/.test(data)) {
        const [ano, mes, dia] = data
          .split('-')
          .map(Number);

        return format(
          new Date(
            ano,
            mes - 1,
            dia
          ),
          'dd/MM/yyyy'
        );
      }

      const dataConvertida =
        new Date(data);

      if (
        isNaN(
          dataConvertida.getTime()
        )
      ) {
        return '-';
      }

      return format(
        dataConvertida,
        'dd/MM/yyyy'
      );
    } catch (error) {
      return '-';
    }
  };

  const converterData = (data) => {
    if (!data) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      const [ano, mes, dia] = data
        .split('-')
        .map(Number);

      return new Date(
        ano,
        mes - 1,
        dia
      );
    }

    const dataConvertida =
      new Date(data);

    if (
      isNaN(
        dataConvertida.getTime()
      )
    ) {
      return null;
    }

    return dataConvertida;
  };

  const pedidoEstaVencido = (pedido) => {
    if (
      pedido.status === 'VENCIDO'
    ) {
      return true;
    }

    if (
      [
        'PAGO',
        'CANCELADO',
        'EXTORNADO',
      ].includes(pedido.status)
    ) {
      return false;
    }

    const dataVencimento =
      converterData(
        pedido.dataVencimento
      );

    if (!dataVencimento) {
      return false;
    }

    const hoje = new Date();

    hoje.setHours(
      0,
      0,
      0,
      0
    );

    dataVencimento.setHours(
      0,
      0,
      0,
      0
    );

    return dataVencimento < hoje;
  };

  const obterStatus = (pedido) => {
    if (
      pedido.status === 'PENDENTE'
      && pedidoEstaVencido(pedido)
    ) {
      return 'VENCIDO';
    }

    return pedido.status;
  };

  const pedidoEhInscricao = (pedido) => {
    return [
      'INSCRICAO',
      'INSCRICAOEXAME',
    ].includes(pedido.origem);
  };

  const podeCancelar = (pedido) => {
    if (!pedidoEhInscricao(pedido)) {
      return false;
    }

    return [
      'PENDENTE',
      'VENCIDO',
    ].includes(
      obterStatus(pedido)
    );
  };

  const podeReemitir = (pedido) => {
    if (!pedidoEhInscricao(pedido)) {
      return false;
    }

    return pedidoEstaVencido(pedido);
  };

  const abrirModalCancelamento = (
    pedido
  ) => {
    setPedidoSelecionado(pedido);

    setMotivoCancelamento('');

    setFeedback({
      tipo: '',
      mensagem: '',
    });

    setModalCancelamento(true);
  };

  const fecharModalCancelamento = () => {
    if (pedidoEmProcessamento) {
      return;
    }

    setModalCancelamento(false);

    setPedidoSelecionado(null);

    setMotivoCancelamento('');
  };

  const cancelarPedido = async () => {
    if (!pedidoSelecionado) {
      return;
    }

    if (!motivoCancelamento.trim()) {
      setFeedback({
        tipo: 'danger',
        mensagem:
          'Informe o motivo do cancelamento.',
      });

      return;
    }

    try {
      setPedidoEmProcessamento(
        pedidoSelecionado.id
      );

      setTipoProcessamento(
        'cancelar'
      );

      setFeedback({
        tipo: '',
        mensagem: '',
      });

      const response =
        await ApiPedidos.cancelarPedido(
          pedidoSelecionado.id,
          motivoCancelamento.trim()
        );

      if (
        response?.status !== 'success'
      ) {
        throw new Error(
          response?.message
          || 'Não foi possível cancelar o boleto.'
        );
      }

      setModalCancelamento(false);

      setPedidoSelecionado(null);

      setMotivoCancelamento('');

      setFeedback({
        tipo: 'success',
        mensagem:
          response?.message
          || 'Boleto cancelado com sucesso.',
      });

      await fetchPagamentos();
    } catch (error) {
      setFeedback({
        tipo: 'danger',
        mensagem:
          error?.message
          || 'Não foi possível cancelar o boleto.',
      });
    } finally {
      setPedidoEmProcessamento(null);

      setTipoProcessamento(null);
    }
  };

  const reemitirPedido = async (
    pedido
  ) => {
    const confirmou = window.confirm(
      'Deseja realmente reemitir este boleto?\n\n'
      + 'O boleto atual será cancelado e um novo boleto '
      + 'será gerado utilizando os valores e descontos vigentes.'
    );

    if (!confirmou) {
      return;
    }

    try {
      setPedidoEmProcessamento(
        pedido.id
      );

      setTipoProcessamento(
        'reemitir'
      );

      setFeedback({
        tipo: '',
        mensagem: '',
      });

      const response =
        await ApiPedidos.reemitirPedido(
          pedido.id
        );

      /*
       * O prazo da inscrição acabou.
       *
       * O backend cancela definitivamente
       * pedido + inscrição.
       */
      if (
        response?.status === 'cancelled'
      ) {
        setFeedback({
          tipo: 'warning',
          mensagem:
            response?.message
            || 'O prazo da inscrição foi encerrado e o pedido foi cancelado.',
        });

        await fetchPagamentos();

        return;
      }

      if (
        response?.status !== 'success'
      ) {
        throw new Error(
          response?.message
          || 'Não foi possível reemitir o boleto.'
        );
      }

      setFeedback({
        tipo: 'success',
        mensagem:
          response?.message
          || 'Boleto reemitido com sucesso.',
      });

      await fetchPagamentos();

      /*
       * A resposta de reemissão vem
       * diretamente do FinanceiroService,
       * portanto o campo neste ponto
       * continua snake_case.
       */
      const novoBoleto =
        response?.fatura?.boleto_pdf;

      if (novoBoleto) {
        window.open(
          novoBoleto,
          '_blank',
          'noopener,noreferrer'
        );
      }
    } catch (error) {
      setFeedback({
        tipo: 'danger',
        mensagem:
          error?.message
          || 'Não foi possível reemitir o boleto.',
      });
    } finally {
      setPedidoEmProcessamento(null);

      setTipoProcessamento(null);
    }
  };

  const obterClasseStatus = (
    pedido
  ) => {
    const status =
      obterStatus(pedido);

    switch (status) {
      case 'PAGO':
        return 'bg-success';

      case 'PENDENTE':
        return 'bg-warning text-dark';

      case 'VENCIDO':
        return 'bg-danger';

      case 'CANCELADO':
        return 'bg-secondary';

      case 'EXTORNADO':
        return 'bg-dark';

      default:
        return 'bg-secondary';
    }
  };

  const renderAcoes = (pedido) => {
    const mostrarReemissao =
      podeReemitir(pedido);

    const mostrarCancelamento =
      podeCancelar(pedido);

    const processando =
      pedidoEmProcessamento
      === pedido.id;

    if (
      !mostrarReemissao
      && !mostrarCancelamento
    ) {
      return '-';
    }

    return (
      <div className="d-flex gap-2 flex-wrap">
        {mostrarReemissao && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={processando}
            onClick={() =>
              reemitirPedido(pedido)
            }
          >
            {processando
              && tipoProcessamento
                === 'reemitir'
              ? 'Reemitindo...'
              : 'Reemitir'}
          </button>
        )}

        {mostrarCancelamento && (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={processando}
            onClick={() =>
              abrirModalCancelamento(
                pedido
              )
            }
          >
            {processando
              && tipoProcessamento
                === 'cancelar'
              ? 'Cancelando...'
              : 'Cancelar'}
          </button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <section>
        <div className="container">
          <p>
            Carregando financeiro...
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section>
        <div className="container">

          {feedback.mensagem && (
            <div
              className={
                `alert alert-${feedback.tipo}`
              }
              role="alert"
            >
              {feedback.mensagem}
            </div>
          )}

          {Array.isArray(pedidos)
            && pedidos.length > 0 ? (

            <div className="table-responsive-financeiro">

              <table className="table responsible">

                <thead>
                  <tr>
                    <th>Tipo</th>

                    <th>
                      Data Emissão
                    </th>

                    <th>
                      Data Vencimento
                    </th>

                    <th>
                      Data Pagamento
                    </th>

                    <th>
                      Valor
                    </th>

                    <th>
                      Situação
                    </th>

                    <th>
                      Boleto
                    </th>

                    <th>
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody>

                  {pedidos.map(
                    (pedido) => {

                      const status =
                        obterStatus(
                          pedido
                        );

                      return (
                        <tr
                          key={pedido.id}
                        >

                          <td>
                            {pedido.origem
                              ?? '-'}
                          </td>

                          <td>
                            {formatarData(
                              pedido.dataCriacao
                            )}
                          </td>

                          <td>
                            {formatarData(
                              pedido.dataVencimento
                            )}
                          </td>

                          <td>
                            {formatarData(
                              pedido.dataPagamento
                            )}
                          </td>

                          <td>
                            <ValorMonetario
                              valor={
                                pedido.valor
                                ?? 0
                              }
                            />
                          </td>

                          <td>
                            <span
                              className={
                                `badge ${obterClasseStatus(
                                  pedido
                                )}`
                              }
                            >
                              {status ?? '-'}
                            </span>
                          </td>

                          <td>

                            {pedido.boletoPdf
                              && ![
                                'CANCELADO',
                                'EXTORNADO',
                              ].includes(
                                status
                              ) ? (

                              <a
                                href={
                                  pedido.boletoPdf
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-main btn-fullwidth rounded-3"
                              >
                                Baixar
                              </a>

                            ) : (
                              '-'
                            )}

                          </td>

                          <td>
                            {renderAcoes(
                              pedido
                            )}
                          </td>

                        </tr>
                      );
                    }
                  )}

                </tbody>

              </table>

            </div>

          ) : (

            <div className="row align-items-center gh-5">

              <div className="col-lg-6">

                <p>
                  Nenhuma anuidade ou
                  inscrição gerada!
                </p>

              </div>

            </div>

          )}

        </div>
      </section>

      {modalCancelamento && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
            >
              <div className="modal-content">

                <div className="modal-header">

                  <h5 className="modal-title">
                    Cancelar boleto
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    disabled={
                      pedidoEmProcessamento
                      !== null
                    }
                    onClick={
                      fecharModalCancelamento
                    }
                    aria-label="Fechar"
                  />

                </div>

                <div className="modal-body">

                  <p>
                    Deseja realmente
                    cancelar o pedido{' '}
                    <strong>
                      #
                      {
                        pedidoSelecionado
                          ?.id
                      }
                    </strong>
                    ?
                  </p>

                  <div
                    className="alert alert-warning"
                  >
                    Ao confirmar, o boleto
                    será cancelado e a
                    inscrição vinculada
                    será removida do
                    evento ou exame.
                  </div>

                  <div className="mb-3">

                    <label
                      htmlFor="motivoCancelamento"
                      className="form-label"
                    >
                      Motivo do
                      cancelamento
                    </label>

                    <textarea
                      id="motivoCancelamento"
                      className="form-control"
                      rows="4"
                      maxLength="1000"
                      value={
                        motivoCancelamento
                      }
                      disabled={
                        pedidoEmProcessamento
                        !== null
                      }
                      onChange={(
                        event
                      ) =>
                        setMotivoCancelamento(
                          event.target
                            .value
                        )
                      }
                      placeholder="Informe o motivo do cancelamento"
                    />

                    <div className="text-end">

                      <small className="text-muted">

                        {
                          motivoCancelamento
                            .length
                        }
                        /1000

                      </small>

                    </div>

                  </div>

                </div>

                <div className="modal-footer">

                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={
                      pedidoEmProcessamento
                      !== null
                    }
                    onClick={
                      fecharModalCancelamento
                    }
                  >
                    Voltar
                  </button>

                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={
                      pedidoEmProcessamento
                      !== null
                      || !motivoCancelamento
                        .trim()
                    }
                    onClick={
                      cancelarPedido
                    }
                  >
                    {pedidoEmProcessamento
                      && tipoProcessamento
                        === 'cancelar'
                      ? 'Cancelando...'
                      : 'Confirmar cancelamento'}
                  </button>

                </div>

              </div>
            </div>
          </div>

          <div
            className="modal-backdrop fade show"
          />
        </>
      )}
    </>
  );
}